import { useState, useEffect, useRef } from "react";
import { useSEO } from "@/hooks/use-seo";
import { useQuery } from "@tanstack/react-query";
import { Search, ChevronDown, ChevronUp, Star, Trophy, Zap, BookOpen, HelpCircle, Gamepad2, TrendingUp, Shield, Loader2, ExternalLink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

interface SlotGame {
  id: number;
  name: string;
  slug: string;
  url: string;
  thumb: string;
  provider: string;
  provider_slug: string;
  rtp: string;
  volatility: string;
  min_bet: string | number;
  max_bet: string | number;
}

interface GamesResponse {
  data: SlotGame[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

const TOP10 = [
  {
    rank: 1,
    name: "Sugar Rush",
    provider: "Pragmatic Play",
    rtp: "95.5%",
    volatility: "Vysoká",
    minStake: "5 Kč",
    maxStake: "2 500 Kč",
    thumb: "https://assets.slotslaunch.com/35001/sugar-rush.jpg",
    desc: "Pestrý slot od Pragmatic Play na téma automatu s cukrovinkami. Spolu se sladkostmi vydává i příjemné peněžní výhry.",
  },
  {
    rank: 2,
    name: "Sweet Bonanza",
    provider: "Pragmatic Play",
    rtp: "96.51%",
    volatility: "Střední",
    minStake: "5 Kč",
    maxStake: "2 500 Kč",
    thumb: "https://assets.slotslaunch.com/5706/sweet-bonanza.jpg",
    desc: "Dětská oslava, cenné výhry a možnost zakoupit bonusové kolo – to vše nabízí hra Sweet Bonanza od Pragmatic Play.",
  },
  {
    rank: 3,
    name: "Fishin' Reels",
    provider: "Pragmatic Play",
    rtp: "95.5%",
    volatility: "Vysoká",
    minStake: "2,5 Kč",
    maxStake: "2 500 Kč",
    thumb: "https://assets.slotslaunch.com/5616/fishin-reels.jpg",
    desc: "Hra se bude líbit milovníkům rybaření, kteří si mohou s Pragmatic Play chytit velkého kapra, aniž by opustili domov.",
  },
  {
    rank: 4,
    name: "Cave of Fortune",
    provider: "BF Games",
    rtp: "95.5%",
    volatility: "Vysoká",
    minStake: "7,5 Kč",
    maxStake: "1 500 Kč",
    thumb: "https://assets.slotslaunch.com/10550/cave-of-fortune.jpg",
    desc: "Najít spolu s jeskynním trollem cennou výhru je reálné. Stačí párkrát roztočit válce v Cave of Fortune od BF Games.",
  },
  {
    rank: 5,
    name: "Oldskool Spin 81",
    provider: "Apollo Games",
    rtp: "95%",
    volatility: "Střední",
    minStake: "2,5 Kč",
    maxStake: "850 Kč",
    thumb: "https://assets.slotslaunch.com/9990/oldskool-spin-81.jpg",
    desc: "Co může být lepšího než klasický ovocný slot? Jedině ovocný slot od Apollo Games za doprovodu disko hudby 80. let.",
  },
  {
    rank: 6,
    name: "Forge of Olympus",
    provider: "Pragmatic Play",
    rtp: "96.25%",
    volatility: "Vysoká",
    minStake: "5 Kč",
    maxStake: "2 500 Kč",
    thumb: "https://assets.slotslaunch.com/15216/forge-of-olympus.jpg",
    desc: "Tento slot dokazuje, že každý je svého štěstí strůjcem. Bůh Hefaistos zodpovídá za vyplácení skutečně velkých výher.",
  },
  {
    rank: 7,
    name: "Dragon's Fire",
    provider: "Red Tiger",
    rtp: "95.25%",
    volatility: "Vysoká",
    minStake: "5 Kč",
    maxStake: "250 Kč",
    thumb: "https://assets.slotslaunch.com/3802/dragons-fire.jpg",
    desc: "Drak střeží poklady, ale odvážným hráčům se může podařit získat velkou výhru v tomto vizuálně poutavém slotu od Red Tiger.",
  },
  {
    rank: 8,
    name: "10,001 Nights",
    provider: "Red Tiger",
    rtp: "95.73%",
    volatility: "Střední",
    minStake: "2,5 Kč",
    maxStake: "6 000 Kč",
    thumb: "https://assets.slotslaunch.com/26196/10001-nights.jpg",
    desc: "Šeherezáda má mnoho tajemství. Když část z nich odhalíte, stanete se skutečným šejkem s nesčetnými poklady v trezorech Red Tiger.",
  },
  {
    rank: 9,
    name: "Bonus Joker 81",
    provider: "Apollo Games",
    rtp: "95.15%",
    volatility: "Střední",
    minStake: "5 Kč",
    maxStake: "300 Kč",
    thumb: "https://assets.slotslaunch.com/26564/bonus-joker-81.jpg",
    desc: "Toto je zajímavá třířadá hra na 4 válcích od Apollo Games, ve které jsou zastoupeny klasické ovocné symboly.",
  },
  {
    rank: 10,
    name: "Hot Spin Megaways",
    provider: "iSoftBet",
    rtp: "95.98%",
    volatility: "Vysoká",
    minStake: "5 Kč",
    maxStake: "500 Kč",
    thumb: "https://assets.slotslaunch.com/24459/hot-spin-megaways.jpg",
    desc: "Tento slot od iSoftBet nabízí 117 649 šancí na výhru v každém spinu. Klasické ovoce je doplněno moderními bonusovými funkcemi.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Jaký je minimální věk pro hraní v online kasinu?",
    a: "Minimální věk je 18 let, ale často se zvyšuje na 21 let v závislosti na zemi registrace kasina.",
  },
  {
    q: "Jak se liší šance na výhru online a v offline automatech?",
    a: "Prakticky se neliší, protože online i offline sloty fungují na základě stejných mechanismů.",
  },
  {
    q: "Existuje strategie, jak zvýšit šance na výhru?",
    a: "Strategie neexistují, ale při správném výběru slotu a uvědomělém řízení bankrollu lze zvýšit pravděpodobnost, že po každé herní seanci zůstanete v plusu.",
  },
  {
    q: "Mohu skutečně změnit RTP a výsledek spinu?",
    a: "To je nemožné, protože všechny výsledky jsou určovány chráněnými RNG. Některé hry mají funkci nastavení volatility.",
  },
];

const SLOT_TYPES = [
  { name: "Klasické hrací automaty a ovocné automaty", desc: "Obvykle se skládají ze 3 válců a jako symboly se používají ovoce. Zvuky napodobují mechanické otáčení prvků automatu." },
  { name: "Video automaty", desc: "Moderní varianta klasických slotů, kde otáčení válců nahradily krásné animace." },
  { name: "3D automaty", desc: "V designu je kladen důraz na grafické prvky." },
  { name: "Progresivní jackpotové automaty", desc: "Hry, ve kterých každá sázka přispívá do jackpotového fondu. Jackpot může vyhrát kterýkoli hráč v náhodném okamžiku." },
  { name: "Megaways Automaty", desc: "Automaty s tisíci možných výherních kombinací." },
  { name: "Cluster Automaty", desc: "Místo výherních linií se používá princip klastrů – shluků stejných symbolů na sousedních polích." },
  { name: "Multi Payline", desc: "Hrací automaty, ve kterých je platba možná na několika liniích najednou." },
  { name: "Bonus Buy Automaty", desc: "Abyste nemuseli čekat na bonusové kolo, můžete na něj vsadit zvýšenou sázku a užívat si proces." },
  { name: "Hold and Win Automaty", desc: "Automaty se systémem sbírání speciálních symbolů. Při shromáždění kolekce se aktivuje bonusové kolo." },
];

const GLOSSARY = [
  { term: "RTP (Návratnost hráči)", desc: "Procento teoretické návratnosti hráči v přepočtu na dlouhodobou perspektivu." },
  { term: "Volatility (Volatilita)", desc: "Udává frekvenci a velikost vyplácených výher: čím vyšší je ukazatel, tím řidší jsou výplaty, ale jejich velikost je větší." },
  { term: "Payline", desc: "Aktivní linie na válcích, na které se započítávají výherní kombinace." },
  { term: "Scatter", desc: "Speciální symbol, který může spouštět bonusy (freespiny) bez ohledu na linii." },
  { term: "Wild", desc: "Zástupný symbol, který nahrazuje ostatní pro vytvoření výherních kombinací." },
  { term: "Bonusové Kolo", desc: "Doplňková hra nebo funkce, spuštěná při splnění určitých podmínek." },
  { term: "Free Spins", desc: "Bezplatná otočení, obvykle aktivovaná bonusy. Výhry z nich se připisují bez další sázky." },
  { term: "Jackpot", desc: "Fixní nebo progresivní hlavní výhra automatu." },
  { term: "Re-spin", desc: "Opakované otočení jednoho nebo několika válců. Často se poskytuje jako bonusová funkce." },
];

const PROVIDERS = [
  { name: "NetEnt", game: "Starburst", desc: "Jeden z průkopníků v oboru, známý pro svou jasnou grafiku a jednoduché, ale poutavé hry s vysokým RTP." },
  { name: "Microgaming", game: "Mega Moolah", desc: "Legendární značka s největšími progresivními jackpoty. Drží rekord ve vyplacené částce – 19 milionů eur." },
  { name: "Pragmatic Play", game: "Big Bass Bonanza", desc: "Moderní poskytovatel s pestrými tématy a volatilními hrami. Nabízí sloty s RTP 96 %+." },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
        data-testid={`faq-automaty-${q.slice(0, 20).replace(/\s/g, "-")}`}
      >
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

function GameCard({ game, onPlay }: { game: SlotGame; onPlay: (game: SlotGame) => void }) {
  return (
    <div
      className="rounded-xl border border-gray-100 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group overflow-hidden"
      data-testid={`game-card-${game.id}`}
    >
      <div className="relative h-40 bg-gray-100 overflow-hidden">
        {game.thumb ? (
          <img
            src={game.thumb}
            alt={game.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#001a4d] to-[#003da5]">
            <Zap className="w-10 h-10 text-white/40" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="font-bold text-gray-900 text-sm leading-snug mb-0.5 truncate" data-testid={`text-game-title-${game.id}`}>
          {game.name}
        </div>
        <div className="text-gray-400 text-xs mb-2" data-testid={`text-game-provider-${game.id}`}>
          {game.provider}
        </div>
        <Button
          size="sm"
          className="hraj-btn w-full font-bold text-xs gap-1"
          style={{ background: "#003da5" }}
          onClick={() => onPlay(game)}
          data-testid={`button-game-hraj-${game.id}`}
        >
          Hraj
          <ExternalLink className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

function GameModal({ game, onClose }: { game: SlotGame; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900" data-testid="text-modal-game-title">{game.name}</h3>
            <span className="text-gray-400 text-xs">{game.provider}</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            data-testid="button-close-game-modal"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <iframe
            src={game.url}
            className="w-full h-[70vh] border-0"
            title={game.name}
            allowFullScreen
            data-testid="iframe-game-demo"
          />
        </div>
      </div>
    </div>
  );
}

function LiveGameGrid() {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [allGames, setAllGames] = useState<SlotGame[]>([]);
  const [activeGame, setActiveGame] = useState<SlotGame | null>(null);
  const perPage = 20;

  const lastProcessedPage = useRef(0);
  const lastSearchTerm = useRef("");

  const { data, isLoading, isFetching, isError } = useQuery<GamesResponse>({
    queryKey: ["/api/games", page, perPage],
    queryFn: async () => {
      const res = await fetch(`/api/games?page=${page}&per_page=${perPage}`);
      if (!res.ok) throw new Error("Failed to fetch games");
      return res.json();
    },
  });

  useEffect(() => {
    if (data?.data && data.meta.current_page !== lastProcessedPage.current) {
      lastProcessedPage.current = data.meta.current_page;
      setAllGames((prev) => {
        const existingIds = new Set(prev.map((g) => g.id));
        const newGames = data.data.filter((g) => !existingIds.has(g.id));
        if (newGames.length === 0) return prev;
        return [...prev, ...newGames];
      });
    }
  }, [data]);

  const filteredGames = searchTerm
    ? allGames.filter(
        (g) =>
          g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          g.provider.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allGames;

  const hasMore = data?.meta ? page < data.meta.last_page : false;
  const total = data?.meta?.total || 0;

  const handleSearch = () => {
    if (searchInput !== lastSearchTerm.current) {
      lastSearchTerm.current = searchInput;
      setSearchTerm(searchInput);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchInput("");
    lastSearchTerm.current = "";
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat podle názvu nebo poskytovatele..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#003da5]/20 focus:border-[#003da5]"
              data-testid="input-search-games"
            />
          </div>
          <Button
            size="sm"
            className="font-semibold px-4"
            style={{ background: "#003da5" }}
            onClick={handleSearch}
            data-testid="button-search-games"
          >
            Hledat
          </Button>
        </div>
        {searchTerm && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-500">
              Nalezeno: {filteredGames.length} her pro &bdquo;{searchTerm}&ldquo;
            </span>
            <button
              onClick={handleClearSearch}
              className="text-xs text-[#003da5] hover:underline"
              data-testid="button-clear-search"
            >
              Vymazat filtr
            </button>
          </div>
        )}
      </div>

      {isError && (
        <div className="text-center py-12 bg-red-50 rounded-xl border border-red-200 mb-6" data-testid="error-games">
          <p className="text-red-600 font-semibold mb-1">Nepodařilo se načíst hry</p>
          <p className="text-red-500 text-sm">Zkuste to prosím znovu později.</p>
        </div>
      )}

      {isLoading && allGames.length === 0 ? (
        <div className="flex items-center justify-center py-16" data-testid="loading-games">
          <Loader2 className="w-8 h-8 text-[#003da5] animate-spin" />
          <span className="ml-3 text-gray-500">Načítání her...</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6" data-testid="games-grid">
            {filteredGames.map((game) => (
              <GameCard key={game.id} game={game} onPlay={setActiveGame} />
            ))}
          </div>

          {filteredGames.length === 0 && !isLoading && !isError && (
            <div className="text-center py-12 text-gray-400" data-testid="text-no-games">
              Žádné hry nebyly nalezeny
            </div>
          )}

          <div className="text-center space-y-3">
            <p className="text-sm text-gray-500" data-testid="text-games-count">
              Zobrazeno {filteredGames.length} z {total.toLocaleString("cs-CZ")} her
            </p>
            {hasMore && !searchTerm && (
              <Button
                variant="outline"
                className="font-semibold gap-2"
                onClick={() => setPage((p) => p + 1)}
                disabled={isFetching}
                data-testid="button-load-more"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Načítání...
                  </>
                ) : (
                  "Načíst další"
                )}
              </Button>
            )}
          </div>
        </>
      )}

      {activeGame && <GameModal game={activeGame} onClose={() => setActiveGame(null)} />}
    </>
  );
}

export default function AutomatyPage() {
  useSEO({ title: "Online sloty a hrací automaty (2026) – nejlepší slot hry | HrajBonus", description: "Online sloty a hrací automaty v ČR (2026): top slot hry zdarma i o peníze, nejlepší RTP automaty, typy slotů a průvodce výběrem pro začátečníky i zkušené hráče. 18+",
  url: "https://hrajbonus.com/automaty", });

  return (
    <div className="min-h-screen bg-[#f5f7fb]">
      <Header />

      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/80 text-xs font-semibold uppercase tracking-widest mb-5">
            <Gamepad2 className="w-3.5 h-3.5 text-[#f5c518]" /> Online automaty 2026
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4" data-testid="text-automaty-title">
            Nejlepší online sloty v Česku
          </h1>
          <p className="text-white/75 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Mezi tisíci sloty může být nalezení toho správného oříškem i pro zkušeného hráče. Prohlédněte si aktuální žebříček nejlepších automatů, sestavený s ohledem na zájmy hráčů z Česka.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="#hry">
              <Button size="default" className="hraj-btn font-bold gap-2" style={{ background: "#ffffff", color: "#003da5" }}>
                Prohlédnout hry <Zap className="hraj-arrow w-4 h-4" />
              </Button>
            </a>
            <a href="#top10">
              <Button size="default" variant="outline" className="hraj-btn border-white/30 text-white font-semibold" style={{ background: "rgba(255,255,255,0.08)" }}>
                TOP 10 slotů
              </Button>
            </a>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ display: "block", verticalAlign: "bottom", height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-12">

        <section id="uvod" data-testid="section-uvod">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-3">Online sloty a hrací automaty – vše, co potřebujete vědět</h2>
            <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
              <p>
                <strong className="text-gray-900">Online sloty</strong> (také označované jako <strong className="text-gray-900">hrací automaty</strong> nebo <strong className="text-gray-900">slot hry</strong>) jsou dnes nejpopulárnějším typem her v online kasinech. Fungují na principu náhodného generátoru čísel (RNG), který zajišťuje spravedlivé výsledky každého spinu, a jsou dostupné jak v mobilních prohlížečích, tak jako aplikace zdarma ke stažení.
              </p>
              <p>
                Moderní online sloty nabízejí stovky tematicky různorodých her – od klasických třívalkových automatů po komplexní video sloty s bonusovými rundami, free spiny a jackpoty. Klíčové parametry, podle kterých hráči vybírají, jsou <strong className="text-gray-900">RTP (Return to Player)</strong> a <strong className="text-gray-900">volatilita</strong> – vysvětlujeme je podrobně níže.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mt-2">
                {[
                  { label: "RTP 96 %+", desc: "Hledejte sloty s vysokým RTP pro lepší dlouhodobou návratnost." },
                  { label: "Free spiny", desc: "Bonusové kola zdarma – nejčastější bonusová funkce u moderních slotů." },
                  { label: "Demo zdarma", desc: "Vyzkoušejte libovolnou slot hru bez vkladu v demo režimu." },
                ].map(item => (
                  <div key={item.label} className="bg-[#f0f5ff] border border-[#003da5]/15 rounded-xl p-4">
                    <div className="font-bold text-[#003da5] text-sm mb-1">{item.label}</div>
                    <p className="text-gray-600 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="hry" data-testid="section-live-games">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2">Top hrací automaty 2026</h2>
            <p className="text-gray-500 text-base">Vyberte si z tisíců her od předních poskytovatelů a hrajte demo zdarma</p>
          </div>
          <LiveGameGrid />
        </section>

        <section id="top10" data-testid="section-top10">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2">Top 10 Online slotů pro rok 2026</h2>
            <p className="text-gray-500 text-base">Na trhu iGamingových služeb je k dispozici mnoho vzrušujících hazardních her. Zjistěte, které automaty si skutečně zaslouží vaši pozornost.</p>
          </div>
          <div className="space-y-4">
            {TOP10.map((slot) => (
              <div
                key={slot.rank}
                className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm overflow-hidden"
                data-testid={`top10-slot-${slot.rank}`}
              >
                <div className="flex items-center gap-4 px-6 py-4 border-b border-[#eef1f7]" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}>
                  <div className="w-8 h-8 rounded-full bg-white/15 border border-white/30 flex items-center justify-center font-black text-white text-base flex-shrink-0">
                    {slot.rank}
                  </div>
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-white/20">
                    <img
                      src={slot.thumb}
                      alt={slot.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-black text-lg leading-tight">{slot.name}</div>
                    <div className="text-white/60 text-xs font-semibold">{slot.provider}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3">
                    <div className="text-center">
                      <div className="text-white/50 text-[10px] uppercase tracking-wider">RTP</div>
                      <div className="text-white font-bold text-sm">{slot.rtp}</div>
                    </div>
                    <div className="w-px h-8 bg-white/20" />
                    <div className="text-center">
                      <div className="text-white/50 text-[10px] uppercase tracking-wider">Volatilita</div>
                      <div className="text-white font-bold text-sm">{slot.volatility}</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{slot.desc}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-[#f3f6fb] rounded-lg p-3">
                      <span className="text-gray-500 text-xs block">RTP</span>
                      <span className="text-gray-900 font-semibold text-sm">{slot.rtp}</span>
                    </div>
                    <div className="bg-[#f3f6fb] rounded-lg p-3">
                      <span className="text-gray-500 text-xs block">Volatilita</span>
                      <span className="text-gray-900 font-semibold text-sm">{slot.volatility}</span>
                    </div>
                    <div className="bg-[#f3f6fb] rounded-lg p-3">
                      <span className="text-gray-500 text-xs block">Min sázka</span>
                      <span className="text-gray-900 font-semibold text-sm">{slot.minStake}</span>
                    </div>
                    <div className="bg-[#f3f6fb] rounded-lg p-3">
                      <span className="text-gray-500 text-xs block">Max sázka</span>
                      <span className="text-gray-900 font-semibold text-sm">{slot.maxStake}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="proc-hrat" data-testid="section-proc-hrat">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#003da5]" /> Proč hrát online automaty a je jejich hraní bezpečné?
            </h2>
            <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
              <p>Online hazardní hry mohou být bezpečné, pokud dáváte přednost spolehlivým stránkám. Mluvíme pouze o poctivých kasinech, která vám umožní bavit se bez rizika, že budete podvedeni.</p>
              <h3 className="font-bold text-gray-900 text-base">Jsou online sloty legální v České republice?</h3>
              <p>Pro hraní je důležité vybírat pouze licencovaná online kasina. Takové platformy nabízejí pouze bezpečné sloty od spolehlivých poskytovatelů, které používají certifikovaný RNG auditovaný organizacemi jako{" "}
              <a href="https://www.ecogra.org/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-ecogra-automaty">eCOGRA</a>. Každý spin v nich zaručuje spravedlivé slosování sázky. V Česku jsou podobné hazardní hry zcela legální. Je však důležité pamatovat na principy zodpovědného hraní, abyste si udrželi kontrolu nad procesem.</p>
              <h3 className="font-bold text-gray-900 text-base">Co jsou online sloty a proč je hrát?</h3>
              <p>Sloty se liší tématem, herními prvky, volatilitou a doplňkovými funkcemi. To vám umožní vybrat si hru, která vám pomůže se uvolnit a bavit se. Dalším bonusem budou peněžní výhry, které si můžete vybrat na kartu.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm"><span className="font-bold">Věděli jste, že?</span> Podle průzkumu UK Gambling Commission téměř 78 % hráčů před registrací v online kasinu kontroluje, zda má platforma licenci. To zdůrazňuje, jak kritická je pro uživatele bezpečnost a transparentnost.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="rtp-volatilita" data-testid="section-rtp-volatilita">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#003da5]" /> RTP, volatilita a bonusové funkce – jak číst parametry slotů
            </h2>
            <div className="space-y-5 text-gray-600 text-sm leading-relaxed">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="bg-[#f0f5ff] border border-[#003da5]/15 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 text-base mb-2">Co je RTP?</h3>
                  <p className="mb-2">RTP neboli <strong className="text-gray-900">Return to Player</strong> udává, kolik procent ze vsazených peněz slot v dlouhodobém horizontu vrátí hráčům. Slot s RTP 96 % vrátí statisticky 96 Kč z každých 100 Kč vsazených.</p>
                  <div className="bg-white border border-[#dde4ee] rounded-lg p-3">
                    <div className="font-bold text-[#003da5] text-sm">Doporučená hranice RTP:</div>
                    <div className="text-gray-700 text-sm">96 % a více = nadprůměrné. Pod 94 % = vyhýbejte se.</div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 text-base mb-2">Co je volatilita?</h3>
                  <p className="mb-2">Volatilita (nebo rozptyl) říká, jak rizikový slot je. Určuje frekvenci výher a jejich velikost – toto je druhý nejdůležitější parametr po RTP.</p>
                  <ul className="space-y-1.5">
                    <li className="flex items-start gap-2"><span className="text-green-600 font-bold flex-shrink-0">Nízká:</span><span>Časté, ale malé výhry. Vhodné pro delší hraní s nižším bankrollem.</span></li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold flex-shrink-0">Střední:</span><span>Vyvážená frekvence i výše výher. Nejoblíbenější segment.</span></li>
                    <li className="flex items-start gap-2"><span className="text-red-600 font-bold flex-shrink-0">Vysoká:</span><span>Vzácné, ale potenciálně velké výhry. Velké riziko vyčerpání bankrollu.</span></li>
                  </ul>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base mb-3">Nejčastější bonusové funkce slotů</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { name: "Free spiny", desc: "Kola zdarma spuštěná scatter symboly. Nejoblíbenější bonusová funkce." },
                    { name: "Wild symboly", desc: "Zastupují libovolný symbol a doplňují výherní kombinace." },
                    { name: "Bonus runda", desc: "Speciální mini-hra s výběrem odměn nebo kola na kolečku." },
                    { name: "Multiplier", desc: "Násobič výher – může znásobit výhru 2x, 5x nebo i 100x." },
                  ].map(f => (
                    <div key={f.name} className="bg-[#f3f6fb] rounded-xl p-4">
                      <div className="font-bold text-gray-900 text-sm mb-1">{f.name}</div>
                      <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="vyber-automatu" data-testid="section-vyber-automatu">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-6 h-6 text-[#003da5]" /> Na co si dát pozor při výběru herního automatu?
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">Sloty se mohou od sebe velmi lišit – od vizuálního zpracování až po sadu bonusových funkcí. Právě tyto vlastnosti tvoří charakter každé hry.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "1. Téma", text: "Každý slot je navržen v určitém tématu. Vývojáři čerpají inspiraci z každodenního života, popkultury, mýtů a legend." },
                { title: "2. Výplaty", text: "Je dobré, když má slot vysokou hodnotu výherních kombinací. Ještě lepší je, když padají dostatečně často." },
                { title: "3. Riziko", text: "Riziko je souhrn volatility a RTP. Tyto ukazatele říkají, jak reálné je získat peníze v dané hře." },
                { title: "4. Pozor na volatilitu", text: "Volatilita ukazuje, jak dlouho musíte čekat na výhru. Nízká volatilita = časté malé výhry. Vysoká = vzácné velké výhry." },
                { title: "5. Věnujte pozornost RTP", text: "Čím vyšší je teoretické procento návratnosti slotu, tím vyšší je pravděpodobnost vrátit investice." },
                { title: "6. Jaký je váš rozpočet?", text: "Úspěšní hráči umí správně spravovat svůj herní rozpočet. Vyberte si slot, který vyhovuje vašemu tempu." },
              ].map((item) => (
                <div key={item.title} className="bg-[#f3f6fb] rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="typy-slotu" data-testid="section-typy-slotu">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Gamepad2 className="w-6 h-6 text-[#003da5]" /> Jaké typy slotů existují?
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">Rozmanitost slotů a herních mechanik vedla k tomu, že uživatelé mají široké pole pro výběr hry.</p>
            <div className="space-y-3">
              {SLOT_TYPES.map((t) => (
                <div key={t.name} className="flex items-start gap-3 p-3 rounded-xl bg-[#f3f6fb]">
                  <div className="w-8 h-8 rounded-lg bg-[#003da5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-4 h-4 text-[#003da5]" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{t.name}</div>
                    <p className="text-gray-600 text-xs leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm"><span className="font-bold">Věděli jste, že?</span> Megaways Slots používají dynamický počet symbolů na každém válci. Díky tomu mají hráči až 117 649 způsobů, jak vyhrát v jednom spinu.</p>
            </div>
          </div>
        </section>

        <section id="jak-zvysit-sance" data-testid="section-jak-zvysit-sance">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#003da5]" /> Jak zvýšit své šance na výhru ve slotech?
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">Výhry ve slotech jsou vždy náhodné. Nikdo nedokáže předpovědět typ výherní kombinace. Existují však způsoby, jak minimalizovat náklady při správném přístupu k sázkám.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "1. Čtěte naše recenze", text: "Recenze ukazují, jak často ten či onen slot přináší výhry." },
                { title: "2. Sledujte, jak hrají streameři", text: "Taková videa poskytují představu o reálných mechanikách slosování každé sázky." },
                { title: '3. Existuje něco jako \u201estrategie pro sloty\u201c?', text: "Ve slotech neexistují lineární výhry ani šance 50/50. Správné řízení rozpočtu a porozumění volatilitě pomáhají dosahovat častých výher." },
                { title: "4. Vyzkoušejte zdarma!", text: "Nejlepší hrací automaty podporují demo režim. S jeho pomocí můžete zdarma prozkoumat všechny nuance mechaniky." },
              ].map((item) => (
                <div key={item.title} className="bg-[#f3f6fb] rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm"><span className="font-bold">Věděli jste, že?</span> Výzkumy ukazují, že hráči častěji volí automaty s vysokým RTP (96 %+). Díky tomuto přístupu zvyšují své šance na návratnost.</p>
            </div>
          </div>
        </section>

        <section id="poskytovatele" data-testid="section-poskytovatele">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-[#003da5]" /> Poskytovatelé slotů, kterým byste měli věnovat pozornost
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">Poskytovatelé jsou společnosti, které vytvářejí sloty: od vývoje RNG až po design hry.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {PROVIDERS.map((p) => (
                <div key={p.name} className="bg-[#f3f6fb] rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#003da5]/10 flex items-center justify-center mx-auto mb-3">
                    <Star className="w-5 h-5 text-[#003da5]" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{p.name}</h3>
                  <div className="text-[#003da5] text-xs font-semibold mb-2">{p.game}</div>
                  <p className="text-gray-600 text-xs leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="slovnicek" data-testid="section-slovnicek">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#003da5]" /> Slovníček slotů – Naučte se hantýrku
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {GLOSSARY.map((g) => (
                <div key={g.term} className="bg-[#f3f6fb] rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{g.term}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pruvodci" data-testid="section-pruvodci">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-2 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#003da5]" /> Průvodci
            </h2>
            <p className="text-gray-500 text-sm mb-5">Podrobné články, které vám pomohou lépe porozumět světu online automatů</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href="/automaty/jak-vyhrat"
                className="group flex flex-col p-5 rounded-xl border border-gray-100 hover:border-[#003da5]/30 hover:bg-[#f0f5ff] transition-colors"
                data-testid="link-pruvodce-jak-vyhrat"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-[#003da5]" />
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#003da5] transition-colors">Jak vyhrát na automatech</span>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Tipy a strategie, jak maximalizovat své šance na výhru při hraní online automatů.</p>
              </a>
              <a
                href="/automaty/sloty-ktere-vyplaceji-nejvice"
                className="group flex flex-col p-5 rounded-xl border border-gray-100 hover:border-[#003da5]/30 hover:bg-[#f0f5ff] transition-colors"
                data-testid="link-pruvodce-sloty-vyplaceji"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-5 h-5 text-[#003da5]" />
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#003da5] transition-colors">Sloty které vyplácejí nejvíce</span>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Přehled automatů s nejvyšším RTP a nejlepšími výplatními poměry pro hráče.</p>
              </a>
            </div>
          </div>
        </section>

        <section id="faq" data-testid="section-automaty-faq">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#003da5]" /> FAQ Sekce
            </h2>
            <div>
              {FAQ_ITEMS.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </section>

      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 pb-4">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium">
          <Shield className="w-3.5 h-3.5" />
          <span>18+ | Hrajte zodpovědně |{" "}
            <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gamcare-automaty">GamCare</a>{" "}|{" "}
            <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gambleaware-automaty">GambleAware</a>
          </span>
        </div>
      </div>

      <Footer />
    </div>
  );
}
